import { Toggle } from "@flanksource-ui/ui/FormControls/Toggle";
import MRTDataTable from "@flanksource-ui/ui/MRTDataTable/MRTDataTable";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Token } from "../../../api/services/tokens";
import { tokensTableColumns } from "./TokensTableColumns";
import TokenDetailsModal from "../TokenDetailsModal";

type TokensTableProps = {
  tokens: Token[];
  isLoading?: boolean;
  isRefetching?: boolean;
  refresh?: () => void;
  showHideAgentsToggle?: boolean;
  defaultHideAgents?: boolean;
  tokenIdSearchParamKey?: string;
};

export default function TokensTable({
  tokens,
  isLoading,
  isRefetching,
  refresh,
  showHideAgentsToggle = true,
  defaultHideAgents = true,
  tokenIdSearchParamKey = "id"
}: TokensTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const columns = useMemo(() => tokensTableColumns, []);

  const tokenId = searchParams.get(tokenIdSearchParamKey) ?? undefined;
  const selectedToken = useMemo(() => {
    return tokens.find((token) => token.id === tokenId);
  }, [tokens, tokenId]);

  const [hideAgents, setHideAgents] = useState(defaultHideAgents);
  const filteredTokens = useMemo(() => {
    if (hideAgents) {
      return tokens.filter((token) => !token.name.startsWith("agent-"));
    } else {
      return tokens;
    }
  }, [tokens, hideAgents]);

  return (
    <div className="flex h-full w-full flex-col gap-4">
      {showHideAgentsToggle && (
        <Toggle
          value={hideAgents}
          label="Hide Agents"
          className="inline-flex items-center"
          onChange={(val) => {
            setHideAgents(val);
          }}
        />
      )}

      <MRTDataTable
        data={filteredTokens}
        columns={columns}
        isLoading={isLoading}
        isRefetching={isRefetching}
        enableServerSideSorting={false}
        onRowClick={(token) => {
          searchParams.set(tokenIdSearchParamKey, token.id);
          setSearchParams(searchParams);
        }}
      />

      {selectedToken && (
        <TokenDetailsModal
          token={selectedToken}
          isOpen={!!selectedToken}
          onClose={() => {
            if (refresh) {
              refresh();
            }
            searchParams.delete(tokenIdSearchParamKey);
            setSearchParams(searchParams);
          }}
        />
      )}
    </div>
  );
}
